/**
 * GitignoreManager — auto-add artifact directory to .gitignore.
 *
 * Ensures the configured artifact output directory is listed
 * in the project's .gitignore. Additive-only (never removes entries).
 */

import { existsSync, readFileSync, appendFileSync } from 'node:fs';
import { dirname, join, relative, resolve } from 'node:path';

/** Hard ceiling on git-root walk depth — see `findGitRoot`. */
const MAX_WALK_DEPTH = 256;

/**
 * Find the repository root by walking up from startDir looking for `.git`.
 * Returns null if no git repository is found.
 *
 * `startDir` is resolved to an absolute path first: a relative artifacts dir
 * (the default is `./test-results/artifacts`) is anchored to `process.cwd()`
 * so the walk climbs the project's real ancestors. Termination uses
 * `dirname(dir) === dir`, which is idempotent at the filesystem root on every
 * platform — unlike comparing against `parse(dir).root`, which is the empty
 * string for a relative path and is never reached, spinning forever.
 */
function findGitRoot(startDir: string): string | null {
  let dir = resolve(startDir);
  // Belt-and-braces: `dirname(dir) === dir` already terminates at the filesystem
  // root, but this reporter runs inside the customer's `playwright test` process,
  // where an unbounded walk is fatal rather than cosmetic. A depth cap makes it
  // structurally impossible for this loop to spin the event loop again, whatever
  // a future path edge case (UNC share, exotic mount) does to the termination
  // condition. Real trees are nowhere near this deep.
  for (let depth = 0; depth < MAX_WALK_DEPTH; depth++) {
    if (existsSync(join(dir, '.git'))) {
      return dir;
    }
    const parent = dirname(dir);
    if (parent === dir) {
      return null; // reached the filesystem root without finding `.git`
    }
    dir = parent;
  }
  return null;
}

/**
 * Ensure the artifact output directory is listed in .gitignore.
 *
 * - If not in a git repo, silently returns (no error).
 * - If the pattern is already present, does nothing.
 * - If missing, appends the pattern with a descriptive comment.
 *
 * @param artifactsDir - Absolute path to the artifacts directory
 */
export function ensureGitignore(artifactsDir: string): void {
  try {
    const gitRoot = findGitRoot(artifactsDir);
    if (!gitRoot) return;

    const gitignorePath = join(gitRoot, '.gitignore');
    const relativePath = relative(gitRoot, artifactsDir).replace(/\\/g, '/');
    const pattern = relativePath.endsWith('/') ? relativePath : `${relativePath}/`;

    if (existsSync(gitignorePath)) {
      const content = readFileSync(gitignorePath, 'utf-8');
      // Check if the pattern (or a parent pattern) already covers the directory
      const lines = content.split('\n').map((l) => l.trim());
      if (lines.some((line) => line === pattern || line === relativePath)) {
        return; // Already covered
      }
    }

    // Append the pattern
    const entry = `\n# TestRelic test artifacts\n${pattern}\n`;
    appendFileSync(gitignorePath, entry, 'utf-8');
  } catch {
    // Never crash — gitignore management is best-effort
  }
}
